import { ValueInput } from '@utils/formattersV2';
import { unifiedPlatformColorPalette } from '@utils/unifiedPlatformColorPalette';
import {
  calculateDataCountByDayOfWeek,
  calculateLatestDate,
  calculateFirstDate,
} from './dates';

export type SplineChartOptionsProps = {
  formatter?: (value: ValueInput) => string;
  color?: string;
  yAxisLabelFormatter?: (value: ValueInput) => string;
  seriesName?: string;
  data: {
    date: string;
    value: number;
  }[];
};

const SYMBOL_RADIUS = 16;

export const splineChartOptions = (
  props: SplineChartOptionsProps
): Highcharts.Options => {
  const dataCountByDayOfWeek = calculateDataCountByDayOfWeek(props.data);
  const latestDate = calculateLatestDate(props.data);
  const firstDate = calculateFirstDate(props.data);

  return {
    credits: {
      enabled: false,
    },

    exporting: {
      enabled: false,
    },

    legend: {
      symbolWidth: SYMBOL_RADIUS,
      symbolHeight: SYMBOL_RADIUS,
      symbolRadius: SYMBOL_RADIUS,
      symbolPadding: 10,
      itemStyle: {
        fontWeight: 'normal',
      },
    },

    title: {
      text: undefined,
    },

    xAxis: {
      type: 'datetime',
      labels: {
        format: '{value:%m/%d/%Y}',
      },
      startOfWeek: Object.entries(dataCountByDayOfWeek).reduce(
        (acc, [dayOfWeek, dataCount]) => {
          if (dataCount > (dataCountByDayOfWeek[acc] ?? 0)) {
            return Number(dayOfWeek);
          }

          return acc;
        },
        0
      ),
      max: latestDate,
      min: firstDate,
    },

    yAxis: {
      min: 0,
      title: {
        text: undefined,
      },
      labels: {
        formatter: props.yAxisLabelFormatter
          ? function () {
              return props.yAxisLabelFormatter
                ? props.yAxisLabelFormatter(Number(this.value))
                : this.value.toString();
            }
          : undefined,
      },
    },

    tooltip: {
      pointFormatter: props.formatter
        ? function () {
            return props.formatter
              ? props.formatter(this.y)
              : (this.y?.toString() ?? '');
          }
        : undefined,
    },

    series: [
      {
        type: 'spline',
        name: props.seriesName ?? 'All Media',
        color: props.color ?? unifiedPlatformColorPalette.chartLightBlue,
        marker: {
          enabled: true,
          symbol: 'circle',
          radius: SYMBOL_RADIUS / 2,
        },
        data: props.data
          .map(dataPoint => ({
            x: Date.parse(dataPoint.date),
            y: dataPoint.value,
          }))
          .sort((a, b) => a.x - b.x),
      },
    ],
  };
};
